/* globals MediaRecorder */
import { compose, onlyUpdateForKeys } from 'recompose'
import BackLink from '../components/BackLink'
import BaseComponent from '../BaseComponent'
import BottomButton from '../components/BottomButton'
import Layout from '../components/Layout'
import PropTypes from 'prop-types'
import React from 'react'
import { bindFunctions } from '../lib/componentUtils'
import injectSheet from 'react-jss'
import videoStyles from '../styles/video'

const VideoRecordElement = compose(
	onlyUpdateForKeys([]),
	injectSheet(videoStyles)
)(({ classes, videoRef, wrapperEl, setWrapperEl }) =>
	<div
		className={classes.wrapper}
		ref={div => {
			if (div) {
				div.style.height = `${div.getBoundingClientRect().width}px`
			}
		}}
	>
		<video ref={videoRef} autoPlay muted className={classes.video} />
	</div>
)

function countdownSecondsLeft(seconds, startDate) {
	return seconds - (new Date().getTime() - startDate.getTime()) / 1000
}

const recordStyes = {
	countdownStart: {
		'font-size': '60px',
		'line-height': '60px',
		color: 'yellow',
		'-webkit-text-stroke': '1px black',
		left: 0,
		right: 0,
		position: 'absolute',
		top: '100px',
		'text-align': 'center'
	},
	countdownStop: {
		'font-size': '20px',
		'line-height': '20px',
		color: 'red',
		'-webkit-text-stroke': '1px black',
		left: '20px',
		position: 'absolute',
		top: '100px',
		'text-align': 'left'
	},
	error: {
		'font-size': '30px',
		color: 'red'
	},
	beforeRecording: {
		filter: 'brightness(0.6) !important'
	}
}
class Record extends BaseComponent {
	constructor() {
		super()

		bindFunctions(
			[
				'updateSecondsUntilRecord',
				'startRecording',
				'updateSecondsUntilStop',
				'stopRecording'
			],
			this
		)

		this.state = {
			secondsUntilRecord: false,
			recording: false,
			secondsUntilStop: false
		}

		this.bitsPerSecond = 768 * 1024
	}

	componentDidMount() {
		this.mediaSource = new MediaSource()

		const constraints = {
			audio: true,
			video: {
				// https://stackoverflow.com/a/27837229
				width: { min: 640, ideal: 640, max: 1280 },
				height: { min: 640, ideal: 640, max: 1280 }
			}
		}

		navigator.getUserMedia(
			constraints,
			stream => {
				this.stream = stream
				if (window.URL) {
					this.gumVideo.src = window.URL.createObjectURL(stream)
				} else {
					this.gumVideo.src = stream
				}
				this.mediaStartedAt = new Date()
				this.countdownInterval = setInterval(this.updateSecondsUntilRecord, 50)
			},
			error => this.setState({ error })
		)
	}

	updateSecondsUntilRecord() {
		const secondsUntilRecord = countdownSecondsLeft(10, this.mediaStartedAt)
		if (secondsUntilRecord > 0) {
			this.setState({ secondsUntilRecord })
		} else {
			this.setState({ secondsUntilRecord: false }).then(() => {
				clearInterval(this.countdownInterval)
				this.startRecording()
			})
		}
	}

	startRecording() {
		const that = this
		function setComponentProperties() {
			that.recordingStartedAt = new Date()
			that.recordedInterval = setInterval(that.updateSecondsUntilStop, 100)
			that.recordedBlobs = []
		}
		function setMediaRecorderHandlers() {
			that.mediaRecorder.ondataavailable = event => {
				if (event.data && event.data.size > 0) {
					that.recordedBlobs.push(event.data)
				}
			}
			that.mediaRecorder.onstop = () => that.props.recordingStopped(that.recordedBlobs)
		}

		this.setState(prevState => {
			if (prevState.recording) {
				return {}
			}

			setComponentProperties()

			const options = { mimeType: 'video/webm', bitsPerSecond: this.bitsPerSecond }
			try {
				this.mediaRecorder = new MediaRecorder(this.stream, options)
			} catch (e0) {
				try {
					options.mimeType = 'video/webm,codecs=vp9'
					this.mediaRecorder = new MediaRecorder(this.stream, options)
				} catch (e1) {
					this.setState({ error: e1 })
					return {}
				}
			}

			setMediaRecorderHandlers()

			this.mediaRecorder.start(10) // collect 10ms of data per blob

			return {
				recording: true
			}
		})
	}

	updateSecondsUntilStop() {
		const secondsUntilStop = countdownSecondsLeft(15, this.recordingStartedAt)
		if (secondsUntilStop > 0) {
			this.setState({ secondsUntilStop })
		} else {
			this.stopRecording()
			clearInterval(this.recordedInterval)
		}
	}

	stopRecording() {
		this.mediaRecorder.stop()
	}

	componentWillUnmount() {
		if (this.countdownInterval) {
			clearInterval(this.countdownInterval)
		}

		if (this.recordedInterval) {
			clearInterval(this.recordedInterval)
		}
	}

	// eslint-disable-next-line complexity
	render() {
		const recording = Boolean(this.state.recording && this.state.secondsUntilStop)
		const { classes, exercise, recordingCanceled } = this.props
		const countdownStartEl = this.state.secondsUntilRecord && !recording
			? <div className={classes.countdownStart}>
					Start in<br />
					{this.state.secondsUntilRecord.toFixed(0)}
				</div>
			: false
		const countdownStopEl = recording
			? <div className={classes.countdownStop}>
					Recording: {this.state.secondsUntilStop.toFixed(0)}
				</div>
			: false
		const className = recording ? '' : classes.beforeRecording
		const videoRecordEl = this.state.error
			? false
			: <div className={className}>
					<VideoRecordElement
						videoRef={video => {
							this.gumVideo = video
						}}
					/>
				</div>
		const errorEl = this.state.error
			? <p className={classes.error}>
					Error using webcam. Check that you have granted permission.
				</p>
			: false
		const bottomButton = recording
			? <BottomButton
					onClick={this.stopRecording}
					content={`Complete recording (${this.state.secondsUntilStop.toFixed(0)})`}
				/>
			: <BottomButton
					onClick={() => {
						this.startRecording()
					}}
					content={`Start recording now`}
				/>
		return (
			<Layout.Container>
				<Layout.Sticker>
					<h1>Recoding for: {exercise.title}</h1>
					<BackLink onClick={recordingCanceled} content="Cancel" />
					{errorEl}
					{videoRecordEl}
					{countdownStartEl}
					{countdownStopEl}
				</Layout.Sticker>
				<Layout.Viewer />
				<Layout.Sticker>
					{bottomButton}
				</Layout.Sticker>
			</Layout.Container>
		)
	}
}
Record.propTypes = {
	exercise: PropTypes.shape({ title: PropTypes.string }),
	recordingCanceled: PropTypes.func.isRequired,
	recordingStopped: PropTypes.func.isRequired,
	classes: PropTypes.object.isRequired
}
export default injectSheet(recordStyes)(Record)



// WEBPACK FOOTER //
// ./src/components/Record.js